import { readonly, ref } from 'vue';
import type { Language } from '@/i18n/languages';

// The platform country is the country platform a client runs on (Brazil, Mexico…): its regulatory
// system — regulator, reports, official data sources, identifiers and features. In the UI it reads as
// the product edition ("IntegrityOne Brasil"). Language currently follows the platform country 1:1.
// Mirrors the backend's PlatformCountry enum / accounts.platform_country_code. Not to be confused with
// the client's registered country (accounts.country_code).
export const SUPPORTED_PLATFORM_COUNTRIES = ['br', 'mx'] as const;
export type PlatformCountry = (typeof SUPPORTED_PLATFORM_COUNTRIES)[number];

interface PlatformCountryConfig {
  name: string; // proper noun in the country's own language — never translated
  language: Language;
  // IANA time zones located in this country, used to guess the platform country before login.
  timeZones: readonly string[];
}

export const PLATFORM_COUNTRIES: Record<PlatformCountry, PlatformCountryConfig> = {
  br: {
    name: 'Brasil',
    language: 'pt',
    timeZones: [
      'America/Sao_Paulo', 'America/Noronha', 'America/Belem', 'America/Fortaleza', 'America/Recife',
      'America/Araguaina', 'America/Maceio', 'America/Bahia', 'America/Campo_Grande', 'America/Cuiaba',
      'America/Santarem', 'America/Porto_Velho', 'America/Boa_Vista', 'America/Manaus',
      'America/Eirunepe', 'America/Rio_Branco',
    ],
  },
  mx: {
    name: 'México',
    language: 'es',
    timeZones: [
      'America/Mexico_City', 'America/Cancun', 'America/Merida', 'America/Monterrey', 'America/Matamoros',
      'America/Chihuahua', 'America/Ciudad_Juarez', 'America/Ojinaga', 'America/Mazatlan',
      'America/Bahia_Banderas', 'America/Hermosillo', 'America/Tijuana',
    ],
  },
};

export interface PlatformCountryOption {
  value: PlatformCountry;
  label: string;
  flagClass: string; // flag-icons CSS class
}

export const platformCountryFlagClass = (platformCountry: PlatformCountry) => `fi fi-${platformCountry}`;

export const PLATFORM_COUNTRY_OPTIONS: PlatformCountryOption[] = SUPPORTED_PLATFORM_COUNTRIES.map((code) => ({
  value: code,
  label: PLATFORM_COUNTRIES[code].name,
  flagClass: platformCountryFlagClass(code),
}));

// Pages (and their menu links) are closed to every platform but Brazil unless they explicitly list
// more in route meta `platformCountries`; 'any' also admits accounts whose platform isn't set (null).
// Mirrors the backend's closed-by-default API guard.
export type PlatformCountryAccess = readonly PlatformCountry[] | 'any';
const DEFAULT_PLATFORM_COUNTRY_ACCESS: readonly PlatformCountry[] = ['br'];

export const isOpenToPlatformCountry = (
  access: PlatformCountryAccess | undefined,
  platformCountry: PlatformCountry | null,
): boolean => {
  if (access === 'any') return true;
  if (platformCountry === null) return false;
  return (access ?? DEFAULT_PLATFORM_COUNTRY_ACCESS).includes(platformCountry);
};

const DEFAULT_PLATFORM_COUNTRY: PlatformCountry = 'br';
const PLATFORM_COUNTRY_STORAGE_KEY = 'platform_country';

export const isPlatformCountry = (value: unknown): value is PlatformCountry =>
  SUPPORTED_PLATFORM_COUNTRIES.includes(value as PlatformCountry);

function readStoredPlatformCountry(): PlatformCountry | null {
  try {
    const storedPlatformCountry = localStorage.getItem(PLATFORM_COUNTRY_STORAGE_KEY);
    return isPlatformCountry(storedPlatformCountry) ? storedPlatformCountry : null;
  } catch {
    return null;
  }
}

// The time zone *name* (not offset) is country-specific, so neighbouring countries don't collide.
function detectFromTimeZone(): PlatformCountry | null {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return SUPPORTED_PLATFORM_COUNTRIES.find((code) => PLATFORM_COUNTRIES[code].timeZones.includes(timeZone)) ?? null;
}

// Region subtag first (es-MX → mx); bare language (es) only when exactly one platform country uses it.
function detectFromBrowserLanguage(): PlatformCountry | null {
  const browserLanguages = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const browserLanguage of browserLanguages) {
    const [language, region] = (browserLanguage ?? '').toLowerCase().split('-');
    if (isPlatformCountry(region)) return region;
    const languageMatches = SUPPORTED_PLATFORM_COUNTRIES.filter((code) => PLATFORM_COUNTRIES[code].language === language);
    if (languageMatches.length === 1) return languageMatches[0]!;
  }
  return null;
}

// Pre-login guess: last platform used → time zone → browser language → Brasil.
// Temporary for signed-in users too, until the platform country is read from the active account.
function resolveInitialPlatformCountry(): PlatformCountry {
  return readStoredPlatformCountry() ?? detectFromTimeZone() ?? detectFromBrowserLanguage() ?? DEFAULT_PLATFORM_COUNTRY;
}

const activePlatformCountryRef = ref<PlatformCountry>(resolveInitialPlatformCountry());
export const activePlatformCountry = readonly(activePlatformCountryRef);

export function setActivePlatformCountry(platformCountry: PlatformCountry): void {
  activePlatformCountryRef.value = platformCountry;
}

export function persistActivePlatformCountry(): void {
  try {
    localStorage.setItem(PLATFORM_COUNTRY_STORAGE_KEY, activePlatformCountryRef.value);
  } catch {
    // Storage unavailable (private mode / blocked): the next visit falls back to detection.
  }
}
